//COPIED FROM CONFLUENCE

import window from 'window-or-global';
// TODO: remove jquery from here and from webpack config once CONFDEV-52616 is done.
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';

import { trigger } from '@confluence-classic/confluence-event_DO_NOT_USE';
import { CONTEXT_PATH } from '@confluence-classic/confluence-urls';
import { getWikiConfluenceToXhtml, getWikiMarkdownToXhtml } from '../utils/rest-api-editor';
import { getOverride } from '@confluence/meta';
import { isDarkFeatureEnabled } from '../utils/dark-features-query-selector';
import store from '../utils/persistent-storage';

import {
	templateWikiMarkupDialog,
	templateOriginalDefaultTempWikiMarkupDialog,
	templatePreviewWikiIframeContent,
	templateWikiErrorSubDialog,
	templateWikiParserSelector,
} from './templates';

// Stylesheet
require('./insert-wiki-markup.css');

// we flipped the switch, so if the key is found it means that the feature is DISABLED
const isWikiMarkdownDialogEnabled = () => !isDarkFeatureEnabled('editor.insert-wiki-markdown');

const PREFERRED_PARSER_STORAGE_KEY = 'insert-wiki-markup-dialog.parser';
const PREFERRED_PARSER_STORAGE_SCOPE = 'global';
const DEFAULT_PARSER_KEY = 'CONFLUENCE';

// supported wiki types and their respective rest apis.
const SUPPORTED_WIKI_TYPES = {
	HELP_REST_API: `${CONTEXT_PATH}/plugins/tinymce/helplink.action`,
	WIKI: {
		CONFLUENCE: {
			api: getWikiConfluenceToXhtml,
			val: 'CONFLUENCE',
			label: 'tinymce.confluence.conf_wikimarkup_confluence_label',
			helpLink: 'help.insert.wiki.markup',
			restApi: '/rest/tinymce/1/wikixhtmlconverter',
		},
		MARKDOWN: {
			api: getWikiMarkdownToXhtml,
			val: 'MARKDOWN',
			label: 'tinymce.confluence.conf_wikimarkup_markdown_label',
			helpLink: 'help.insert.wiki.markup.markdown',
			restApi: '/rest/tinymce/1/markdownxhtmlconverter',
		},
	},
};

const settings = {
	dialog: {
		id: 'insert-wiki-markup-dialog',
		content: '', // Content requires translations -> it can only be set when plugin initialises (when tinymce is available).
		width: 980,
		height: 500,
		popup: false,
		name: 'confluence.conf_wikimarkup_desc',
	},
	markupEditId: 'insert-wiki-textarea',
	previewAreaId: 'insert-wiki-textarea-preview',
	selectionMenuId: 'wiki-parser-selection-tool',
	serverRequestTimer: 300,
	entityId: getOverride('content-id'),
	spaceKey: getOverride('space-key'),
};

const cachedElement = {
	/**
	 * Caches jQuery elements selector.
	 *
	 * eg:
	 * cachedElement.getElement('#selector');
	 *
	 * @param selector
	 * @returns {*}
	 */
	getElement: (selector) => {
		cachedElement[selector] = cachedElement[selector] || $(selector);

		return cachedElement[selector];
	},
};

// the body reference on the preview iframe.
let $previewIframeBody = null;

/**
 * Returns the config of the markup parser previously selected by the user, or Confluence wiki markup as the default.
 *
 * @returns {*} - the config object for the preferred wiki format
 */
const getPreferredParser = () => {
	const preferredParserKey = store.get(
		PREFERRED_PARSER_STORAGE_KEY,
		PREFERRED_PARSER_STORAGE_SCOPE,
		DEFAULT_PARSER_KEY,
	);
	return SUPPORTED_WIKI_TYPES.WIKI[preferredParserKey];
};

/**
 * Creates the wiki selector menu from the template and returns reference to it
 *
 * @param {function} translate Injected translate function (usually ed.translate).
 * @param preferredParser the config object for the preferred wiki format
 * @returns {*|HTMLElement} - the select jquery html reference
 */
const createWikiSelectorMenu = (translate, preferredParser) => {
	const wikiSelectorMenuTemplate = $(
		templateWikiParserSelector(translate, preferredParser, SUPPORTED_WIKI_TYPES.WIKI),
	);

	const menuBar = $('#insert-wiki-insert-title');

	menuBar.append(wikiSelectorMenuTemplate);

	return $(`#${settings.selectionMenuId}`);
};

/**
 * Creates the preview area by adding a new iframe with the same resources as the editor.
 *
 * @param parentId
 * @returns {*|HTMLElement} - the iframe body reference
 */
const createPreviewArea = (parentId) => {
	// getting resources from the editor
	const context = $('#wysiwygTextarea_ifr').contents().find('head').html();

	const iframe = $("<iframe tabindex='-1' id='insert-wiki-markup-preview' />");

	// add iframe to the preview area
	$(`#${parentId}`).append(iframe);

	// once the iframe is added to the dom we can refer to it
	const iframeRef = iframe.contents();

	// get the correct reference for 'document'.
	const doc = iframeRef[0];

	// write the html template to the document
	doc.open();
	doc.write(templatePreviewWikiIframeContent);
	doc.close();

	// add html Resources to the iFrame head element.
	iframeRef.find('head').html(context);

	// store the body reference
	$previewIframeBody = iframeRef.find('body');
};

/**
 * Returns the parser that is currently selected in the dialog, or the stored parser from a previous selection.
 *
 * @returns {string|string}
 */
const getSelectedParser = () => {
	const optionMenu = $(`#${settings.selectionMenuId}`);
	return optionMenu.length ? optionMenu.val() : getPreferredParser().val;
};

/**
 * Get the converted text form the conversion service.
 *
 * @param option.textToConvert - Text to convert
 * @param option.api - Function to use for API call
 * @param option.successCallback
 * @param option.errorCallback
 */
const getConvertedText = (option) => {
	option = option || {};

	const conversionData = {
		wiki: option.textToConvert,
		entityId: settings.entityId,
		spaceKey: settings.spaceKey,
	};

	option.api(JSON.stringify(conversionData), (error, response) => {
		if (error) {
			if (process.env.NODE_ENV !== 'production') {
				console.log('Error when fetching XHTML', error);
			}
			option.errorCallback(error);
			return;
		}
		option.successCallback(response);
	});
};

/**
 * Enables the property panel for image manipulation by giving a class to images.
 *
 * @param {string} data
 * @returns {string} transformed data
 */
const enableImageProperyPanel = (originalData) => {
	const $data = $(originalData);
	$data.find('img').addClass('confluence-embedded-image'); // CONFDEV-20109 - Enabling the property panel by post processing.

	// We need now to convert it back to a string
	// NOTE: the reason why we don't use .outerHTML is because it would only work for a single node and not for
	// a collection of nodes example "</p>foo</p><h1>bar</h1>" since we can't call the outerHTML of that.
	const transformedData = $('<div>').append($data.clone()).html();

	return transformedData;
};

/**
 * Updates the preview with the parsed result from the text response from the rest conversion API.
 * In case there is an empty value on the markupEditArea than update the preview accordingly without
 * doing a server an unnecessary request.
 *
 */
const updatePreview = () => {
	const renderElement = $(`#${settings.markupEditId}`);
	const text = renderElement.val();
	if (!$.trim(text)) {
		$previewIframeBody.html(text);
	} else {
		// we only try to parse if we have non empty values
		getConvertedText({
			api: SUPPORTED_WIKI_TYPES.WIKI[getSelectedParser()].api,
			textToConvert: text,
			successCallback: (data) => {
				$previewIframeBody.html(data);
			},
			errorCallback: () => {
				$previewIframeBody.html(renderElement.val());
			},
		});
	}
};

/**
 * Adds the appropriate hash for the helpLink of the current selected parser.
 * @param {String} selectedParser - Key value for the current selected parser.
 */
const updateHelpLink = (selectedParser) => {
	const helpLinkElement = $(`#${settings.dialog.id}`).find('.dialog-help-link');
	const helpLink = SUPPORTED_WIKI_TYPES.WIKI[selectedParser].helpLink;

	helpLinkElement.load(SUPPORTED_WIKI_TYPES.HELP_REST_API, {
		linkUrlKey: helpLink,
	});
};

/**
 * Add to Storage user selected parser and also updated preview.
 */
const updateParser = () => {
	const preferredParserKey = getSelectedParser();

	store.set(
		PREFERRED_PARSER_STORAGE_KEY,
		PREFERRED_PARSER_STORAGE_SCOPE,
		DEFAULT_PARSER_KEY,
		preferredParserKey,
	);
	updateHelpLink(preferredParserKey);
	updatePreview();
};

/**
 * The problem is that we currently insert the HTML we generate straight
 * inside a P tag (where the cursor is currently in).
 *
 * refer to CONFDEV-1548.
 *
 * @param editor
 * @param params
 */
const splitParas = (editor /*,params*/) => {
	window.tinymce.each(editor.dom.select('p > p'), (p) => {
		try {
			editor.dom.split(p.parentNode, p);
		} catch (ex) {
			// IE can sometimes fire an unknown runtime error so we just ignore it
			if (process.env.NODE_ENV !== 'production') {
				console.error('insertwikimarkup - splitParas()', ex);
			}
		}
	});
};

/**
 * Copied from AJS
 * @param str
 */
function escapeHtml(str) {
	if (typeof str !== 'string') {
		return '';
	}
	const special = {
		'<': '&lt;',
		'>': '&gt;',
		'&': '&amp;',
		"'": '&#39;',
		'`': '&#96;',
	};
	return str.replace(/[&"'<>`]/g, (str) => {
		if (typeof special[str] === 'string') {
			return special[str];
		}
		return '&quot;';
	});
}

const plugger = {
	init: (ed, url) => {
		settings.dialog.content = isWikiMarkdownDialogEnabled()
			? templateWikiMarkupDialog((s) => ed.translate(s))
			: templateOriginalDefaultTempWikiMarkupDialog;

		/**
		 * Insert the transformed content into the editor itself and close window
		 *
		 * @param data
		 */
		const insertToEditorAndCloseWindow = (data) => {
			const dialogId = settings.dialog.id;
			const parser = getSelectedParser();

			ed.windowManager.setBusy(dialogId, false);

			window.tinymce.EditorManager.activeEditor.windowManager.close(dialogId);

			if (isWikiMarkdownDialogEnabled()) {
				// Track how many users are using wiki/markdown.
				trigger('analytics', { name: `insert-wiki-markup-dialog.${parser}` });

				if (parser === 'MARKDOWN') {
					data = enableImageProperyPanel(data);
				}
			}
			ed.execCommand('mceInsertContent', false, data);
			splitParas(ed);
		};

		/**
		 * Handle error by displaying a inline dialog message when the server returns an invalid response or
		 * the request times out.                 *
		 *
		 * @param XMLHttpRequest
		 * @param textStatus
		 * @param errorThrown
		 */
		const erroredConversion = (XMLHttpRequest, textStatus, errorThrown) => {
			ed.windowManager.setBusy(settings.dialog.id, false);

			const warningMessage = window.tinymce.EditorManager.activeEditor.translate(
				'tinymce.confluence.conf_wikimarkup_conversion_errors',
			);
			const exceptionMessage =
				textStatus === 'timeout'
					? window.tinymce.EditorManager.activeEditor.translate(
							'tinymce.confluence.conf_wikimarkup_timeout',
						)
					: `${escapeHtml(textStatus)} : ${escapeHtml(errorThrown)}`;

			const errorTemplate = templateWikiErrorSubDialog(warningMessage, exceptionMessage);

			window.tinymce.EditorManager.activeEditor.windowManager.alert(errorTemplate);
		};

		// Register commands
		ed.addCommand('InsertWikiMarkup', () => {
			const preferredParser = getPreferredParser();

			// Creates the main dialog
			ed.windowManager.open(settings.dialog, {
				buttons: [
					{
						label: 'confluence_dlg.insertbutton.title',
						action: () => {
							ed.windowManager.setBusy(settings.dialog.id, true);
							getConvertedText({
								api: preferredParser.api,
								successCallback: insertToEditorAndCloseWindow,
								errorCallback: erroredConversion,
								textToConvert: $(`#${settings.markupEditId}`).val(),
								setBusy: true,
							});
						},
					},
				],
				plugin_url: url,
				helpLink: preferredParser.helpLink,
				hintText: 'confluence.conf_wikimarkup_hint',
				cssClass: 'insert-wiki-markup-panel',
			});

			if (isWikiMarkdownDialogEnabled()) {
				// the preview area need to be created as an iframe in order to share same resources as the editor.
				createPreviewArea(settings.previewAreaId);

				// create the selection menu and attach events to update the preview
				createWikiSelectorMenu((s) => ed.translate(s), preferredParser).on('change', updateParser);

				// add events to the markupEdit area to update the preview
				let updatePreviewTimeout;
				function debouncedUpdatePreview() {
					clearTimeout(updatePreviewTimeout);
					updatePreviewTimeout = setTimeout(updatePreview, settings.serverRequestTimer);
				}
				$(`#${settings.markupEditId}`).on('keydown', debouncedUpdatePreview);
			}
		});

		ed.addButton('insertwikimarkup', {
			title: 'confluence.conf_wikimarkup_desc',
			cmd: 'InsertWikiMarkup',
		});
	},

	getInfo: () => {
		return {
			longname: 'InsertWikiMarkip',
			author: 'Atlassian',
			authorurl: 'http://www.atlassian.com',
			infourl: 'http://www.atlassian.com/',
			version: '1.1',
		};
	},
};

export function wikiMarkup() {
	window.tinymce.create('tinymce.plugins.InsertWikiMarkupPlugin', plugger);
	window.tinymce.PluginManager.add(
		'insertwikimarkup',
		window.tinymce.plugins.InsertWikiMarkupPlugin,
	);
}

export function wikiMarkupWRM() {
	return ['wr!confluence.web.resources:core'];
}
